import React, { useState, useEffect, useCallback } from 'react';
import { Trash2, RotateCcw, AlertTriangle, Loader2 } from 'lucide-react';
import api from '../utils/api';
import { Button } from './ui/button';
import { toast } from 'sonner';

/**
 * Admin-only dashboard widget that lists soft-deleted revenues from the past 7
 * days and lets the admin restore them with one click.
 * Revenues older than 7 days are auto-purged by the daily cron job.
 */
const DeletedRevenuesWidget = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [restoring, setRestoring] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const res = await api.get('/revenues/deleted/recent?days=7');
      setData(res.data);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const handleRestore = async (rev) => {
    if (!window.confirm(`استعادة إيراد ${rev.amount} د.إ للحلاق ${rev.barber_name || '?'}؟`)) return;
    setRestoring(rev.id);
    try {
      const res = await api.post(`/revenues/${rev.id}/restore`);
      toast.success(res.data?.message || 'تمت الاستعادة');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'فشل الاستعادة');
    } finally {
      setRestoring(null);
    }
  };

  if (loading || !data || data.count === 0) return null;

  const daysLeft = (purgeAt) => {
    if (!purgeAt) return 7;
    try {
      const ms = new Date(purgeAt).getTime() - Date.now();
      return Math.max(0, Math.ceil(ms / (24 * 60 * 60 * 1000)));
    } catch {
      return 7;
    }
  };

  return (
    <div
      className="bg-card border border-red-500/30 rounded-xl p-4 shadow-lg"
      data-testid="deleted-revenues-widget"
    >
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Trash2 className="w-5 h-5 text-red-400" />
          <h3 className="font-bold">سلة الإيرادات المحذوفة</h3>
          <span className="px-2 py-0.5 rounded-full bg-red-500/20 text-red-400 text-xs font-bold">
            {data.count}
          </span>
        </div>
      </div>

      <p className="text-xs text-muted-foreground mb-3">
        تُحفظ الإيرادات المحذوفة 7 أيام قبل الحذف النهائي. يمكنك استعادتها من هنا.
      </p>

      <div className="space-y-1.5 max-h-72 overflow-y-auto">
        {data.items.slice(0, 10).map((rev) => {
          const daysRemaining = daysLeft(rev.purge_at);
          const urgent = daysRemaining <= 2;
          return (
            <div
              key={rev.id}
              className={`flex items-center justify-between gap-2 rounded px-3 py-2 text-sm transition-colors ${
                urgent ? 'bg-red-500/10 border border-red-500/30' : 'bg-secondary/30'
              }`}
              data-testid={`deleted-row-${rev.id}`}
            >
              <div className="flex items-center gap-2 min-w-0 flex-1 flex-wrap">
                <span className="font-bold text-primary whitespace-nowrap">
                  {Number(rev.amount).toFixed(2)} د.إ
                </span>
                <span className="text-xs text-muted-foreground whitespace-nowrap">
                  · {(rev.date || rev.created_at || '').substring(0, 10)}
                </span>
                <span className="text-xs text-amber-400 whitespace-nowrap">
                  · {rev.barber_name || '?'}
                </span>
                {rev.description && (
                  <span className="text-xs text-muted-foreground/80 truncate">
                    · {rev.description}
                  </span>
                )}
                <span
                  className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full whitespace-nowrap ${
                    urgent
                      ? 'bg-red-500/30 text-red-200 font-bold'
                      : 'bg-amber-500/20 text-amber-300'
                  }`}
                  title={`سيُحذف نهائياً في ${(rev.purge_at || '').substring(0, 10)}`}
                >
                  {urgent && <AlertTriangle className="w-3 h-3" />}
                  متبقي {daysRemaining} يوم
                </span>
                {rev.deleted_by_name && (
                  <span className="text-[10px] text-muted-foreground whitespace-nowrap">
                    · حذف بواسطة {rev.deleted_by_name}
                  </span>
                )}
              </div>
              <Button
                size="sm"
                variant="ghost"
                disabled={restoring === rev.id}
                className="h-7 text-xs text-emerald-400 hover:bg-emerald-500/10 flex-shrink-0"
                onClick={() => handleRestore(rev)}
                data-testid={`restore-${rev.id}`}
              >
                {restoring === rev.id ? (
                  <Loader2 className="w-3 h-3 animate-spin" />
                ) : (
                  <>
                    <RotateCcw className="w-3 h-3 me-1" />
                    استعادة
                  </>
                )}
              </Button>
            </div>
          );
        })}
        {data.items.length > 10 && (
          <p className="text-[11px] text-center text-muted-foreground pt-1">
            و {data.items.length - 10} إيراد آخر…
          </p>
        )}
      </div>
    </div>
  );
};

export default DeletedRevenuesWidget;
